/* istanbul ignore file */

import { Box, Flex, Text, TertiaryButton } from '@moonpig/launchpad-components'
import { IconSystemChevronRight } from '@moonpig/launchpad-assets'
import { EventObject, useTrackGAEventOnce } from '@moonpig/web-core-analytics'
import { system as s } from '@moonpig/launchpad-system'
import { breakpointUp, breakpointDown, styled } from '@moonpig/launchpad-utils'
import React, { FC, useEffect } from 'react'
import { useInView } from 'react-intersection-observer'
import { PromoTileCta } from '../../types'
import { SponsoredPill } from '../SponsoredPill'
import { getProductImageDimensions } from '../../utils/getProductImageDimensions'
import { STANDARD_CARD_SIZE_ID } from '../../constants'

const StyledBox = styled(Box)<{
  isCardGallery: boolean
  $paddingBottom: number
  minWidth: string
}>`
  display: ${props => (props.isCardGallery ? null : 'flex')};
  flex-direction: ${props => (props.isCardGallery ? null : 'column')};
  height: ${props => (props.isCardGallery ? null : '100%')};
  position: relative;
  min-height: 200px;

  &:before {
    ${breakpointUp('lg')} {
      display: ${props => (props.isCardGallery ? 'block' : null)};
      height: ${props => (props.isCardGallery ? '0' : null)};
      content: '';
      padding-bottom: ${props =>
        props.isCardGallery ? `calc(${props.$paddingBottom}% - 20px)` : null};
    }
  }
`

const StyledAdBox = styled(Box)<{ isCardGallery: boolean }>`
  ${s({
    border: '1px solid',
    borderRadius: 4,
    borderColor: 'colorBlack20',
    boxShadow: 2,
  })}

  position: relative;
  left: ${props => (props.isCardGallery ? '8px' : null)};
  width: ${props => (props.isCardGallery ? 'calc(100% - 16px)' : '100%')};
  height: ${props => (props.isCardGallery ? 'calc(100% - 16px)' : '100%')};
  overflow: hidden;

  ${breakpointUp('lg')} {
    position: ${props => (props.isCardGallery ? 'absolute' : null)};
    top: ${props => (props.isCardGallery ? '0px' : null)};
  }
`

const Wrapper = styled.div<{ isCardGallery: boolean }>`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;

  ${breakpointUp('lg')} {
    top: ${props => (props.isCardGallery ? '100%' : null)};
    transform: ${props => (props.isCardGallery ? 'translateY(-100%)' : null)};
  }
`

const StyledCallToAction = styled(TertiaryButton)`
  text-decoration: none;
`

const BorderBoxDesktop = styled(Box)`
  ${s({
    paddingLeft: 6,
    borderTop: 1,
    borderColor: 'colorBorder04',
    flexShrink: 0,
  })}

  ${breakpointDown('lg')} {
    display: none;
  }

  ${breakpointUp('xxl')} {
    ${s({
      paddingLeft: 8,
    })}
  }
`

const BorderBoxMobile = styled(Box)`
  ${s({
    paddingLeft: 6,
    borderColor: 'colorBorder04',
    borderTop: 1,
    flexShrink: 0,
  })}

  ${breakpointUp('lg')} {
    display: none;
  }
`

const ImageWrapper = styled.div<{ isCardGallery: boolean; image: string }>`
  position: relative;
  width: ${props =>
    props.isCardGallery ? 'calc(50% - 16px)' : 'calc(50% - 8px)'};
  flex-shrink: 0;
  overflow: hidden;
  background: ${props => `url(${props.image}) 50% 50%`};
  background-size: cover;

  ${breakpointUp('md')} {
    width: ${props =>
      props.isCardGallery ? 'calc(33.333% - 24px)' : 'calc(33% - 8px)'};
  }

  ${breakpointUp('lg')} {
    width: ${props =>
      props.isCardGallery ? 'calc(50% - 24px)' : 'calc(50% - 8px)'};
  }
`

const ContentWrapper = styled(Flex)<{ isCardGallery: boolean }>`
  min-height: 200px;

  ${breakpointDown('lg')} {
    min-height: ${props => (props.isCardGallery ? '280px' : '400px')};
  }
`

type PromoTileProps = {
  title: string
  description: string
  sponsored: boolean
  image: {
    src: string
    description: string | undefined
  }
  cta: PromoTileCta | undefined
  isCardGallery: boolean
}

const calculatePaddingBottom = () => {
  const dimensions = getProductImageDimensions(
    true,
    STANDARD_CARD_SIZE_ID,
    false,
  )

  return (dimensions.height / (2 * dimensions.width)) * 100
}

const StyledSubText = styled(Text)`
  ${s({
    display: 'block',
    maxWidth: '220px',
    maxHeight: '100px',
    overflow: 'hidden',
    flexGrow: 1,
    typography: 'typeBodyText',
    color: 'colorTextLabel',
    alignSelf: 'top',
    mb: 0,
  })}

  /* stylelint-disable value-no-vendor-prefix */
  display: -webkit-box;
  /* stylelint-disable property-no-vendor-prefix */
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  /* stylelint-enable */
`
const impressionEventData = (cta: PromoTileProps['cta']): EventObject => ({
  event: 'impression',
  content_data: {
    content_type: 'view sponsored tile',
  },
  event_data: {
    label: `${cta?.url} | ${cta?.title}`,
    action: 'view sponsored tile',
    category: 'search',
    non_interaction: true,
  },
})

export const PromoTile: FC<PromoTileProps> = ({
  title,
  description,
  sponsored,
  image,
  cta,
  isCardGallery,
}) => {
  const { trackGAEventOnce } = useTrackGAEventOnce()

  const trackClick = () => {
    return trackGAEventOnce({
      event: 'select_content',
      content_data: {
        content_type: `search | sponsored tile click | ${cta?.title.toLowerCase()}`,
      },
      // For GA3 backward compatibility
      event_data: {
        category: 'search',
        action: 'select sponsored tile',
        label: `${cta?.url} | ${cta?.title.toLowerCase()}`,
        non_interaction: true,
        value: undefined,
      },
      // Web only //
      discount_data: undefined,
      ecommerce: undefined,
      error_data: undefined,
    })
  }

  const { ref, inView } = useInView({
    triggerOnce: true,
  })

  useEffect(() => {
    if (inView) {
      trackGAEventOnce(impressionEventData(cta))
    }
  }, [inView, trackGAEventOnce, cta])

  const handleClick = () => {
    trackClick()

    if (cta?.onClick) {
      cta.onClick()
    }
    if (cta?.url && !cta?.onClick) {
      window.location.href = cta?.url
    }
  }

  return (
    <Box
      tabIndex={0}
      ref={ref}
      aria-label="sponsored product tile"
      data-testid="promo-tile-container"
      height="100%"
    >
      <StyledBox
        minWidth="0"
        isCardGallery={isCardGallery}
        $paddingBottom={calculatePaddingBottom()}
      >
        <StyledAdBox boxShadow={2} isCardGallery={isCardGallery}>
          <Wrapper isCardGallery={isCardGallery}>
            <Flex flexDirection="row" flex={1}>
              <ImageWrapper
                aria-describedby={image.description}
                isCardGallery={isCardGallery}
                image={image.src}
                data-testid="promo-tile-image"
              />
              <ContentWrapper
                isCardGallery={isCardGallery}
                flexDirection="column"
                justifyContent="space-between"
              >
                <Box
                  padding={{
                    xs: 6,
                    xxl: 8,
                  }}
                >
                  {sponsored && (
                    <Box
                      alignSelf="top"
                      mb={{
                        xs: 4,
                        md: 6,
                      }}
                    >
                      <SponsoredPill />
                    </Box>
                  )}
                  <Box height="100%">
                    <Box>
                      <Text
                        typography="typeDisplay04"
                        mb={4}
                        tabIndex={0}
                        textAlign="left"
                        color="colorTextLabel"
                        minWidth="50%"
                      >
                        {title}
                      </Text>
                    </Box>
                    <Box>
                      <StyledSubText as="p">{description}</StyledSubText>
                    </Box>
                  </Box>
                </Box>
                <BorderBoxDesktop>
                  <StyledCallToAction
                    trailingIcon={<IconSystemChevronRight />}
                    data-testid="ad-tile-cta-desktop"
                    onClick={handleClick}
                  >
                    {cta?.title}
                  </StyledCallToAction>
                </BorderBoxDesktop>
              </ContentWrapper>
            </Flex>
            <BorderBoxMobile>
              <StyledCallToAction
                trailingIcon={<IconSystemChevronRight />}
                data-testid="ad-tile-cta-mobile"
                onClick={handleClick}
              >
                {cta?.title}
              </StyledCallToAction>
            </BorderBoxMobile>
          </Wrapper>
        </StyledAdBox>
      </StyledBox>
    </Box>
  )
}
